/**
 * Unified Store - Zustand Store with Slices
 * Shared between Quote Builder and POS Interface
 */

import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createIDBStorage } from './idbStorage';
import { calculateTax } from './taxCalculations';
import { isWarrantyProduct } from '../utils/warranty';

// ============================================================================
// TAX & PRICING UTILITIES
// ============================================================================

const roundCurrency = (value) => Math.round(value * 100) / 100;

// ============================================================================
// IDEMPOTENCY KEY
// ============================================================================

/**
 * The cart's idempotency key, minted per CART-CONTENT VERSION.
 *
 * The point is to survive a retry of an unchanged cart while never letting two
 * genuinely different sales share a key. The failure it closes: the server commits,
 * the 201 is lost to a network blip, the cashier taps Complete again — and because
 * the Moneris capture happens BEFORE the sale POST, that used to record a second
 * sale against a single capture.
 *
 * Rotates on anything that changes what is owed (add/remove/qty/discount/promotion/
 * fulfillment/trade-in/load/clear). Does NOT rotate on who or how (customer,
 * salesperson, notes, tax province, hold/recall) — none of those change the amount,
 * so a retry after one of them is still the same sale.
 *
 * Minted lazily on first read so an untouched cart does not churn UUIDs.
 */
const newClientTransactionId = () =>
  (globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      }));

const calculateItemTotal = (item) => {
  const basePrice = item.unitPriceCents * item.quantity;
  const discountAmount = item.discountPercent
    ? Math.round(basePrice * (item.discountPercent / 100))
    : (item.discountAmountCents || 0);
  return basePrice - discountAmount;
};

const calculateMargin = (revenue, cost) => {
  if (revenue === 0) return 0;
  return roundCurrency(((revenue - cost) / revenue) * 100);
};

// ============================================================================
// CUSTOMER SLICE
// ============================================================================

const createCustomerSlice = (set) => ({
  // State
  customer: null,
  customerPricing: null, // Special pricing rules for this customer
  customerHistory: [], // Recent transactions

  // Actions
  setCustomer: (customer) => set((state) => {
    state.customer = customer;
    // Clear customer-specific pricing when customer changes
    if (!customer) {
      state.customerPricing = null;
    }
  }),

  setCustomerPricing: (pricing) => set((state) => {
    state.customerPricing = pricing;
  }),

  loadCustomerHistory: async (_customerId) => {
    // Will be populated by API call
    set((state) => {
      state.customerHistory = [];
    });
  },

  clearCustomer: () => set((state) => {
    state.customer = null;
    state.customerPricing = null;
    state.customerHistory = [];
  }),
});

// ============================================================================
// CART/LINE ITEMS SLICE
// ============================================================================

const createCartSlice = (set, get) => ({
  // State
  items: [],
  // Which userId owns the current cart. Stamped by AuthContext on every
  // successful auth (PIN or credentials); the PIN-unlock path compares the
  // authenticating user against this to decide preserve (same user, mid-sale
  // recovery) vs clear (different user taking over the terminal).
  // Deliberately NOT reset by clearCart/resetAll: it tracks the session's
  // user, so items added after a hold/clear stay owned by whoever is signed
  // in — only a new auth restamps it.
  cartOwnerId: null,
  quoteId: null, // If loaded from a quote
  quoteNumber: null,
  sourceType: null, // 'quote' | 'pos' | 'new'
  notes: '',
  internalNotes: '',
  salespersonId: null,
  salespersonName: '',
  commissionSplit: null, // { enabled, primaryPct, secondaryRepId, secondaryPct }
  selectedFulfillment: null, // { type, fee, address, ... }
  tradeIns: [], // Trade-in assessments
  loading: false,
  error: null,
  quoteFinancingInfo: null,

  // Idempotency key for the CURRENT cart contents. null = not yet minted; the
  // getter mints on demand. Rotated by every amount-changing action below.
  clientTransactionId: null,

  // Server-priced view of this cart version (item 3c). null until a preview lands.
  // { fingerprint, totals, lines, mismatches, adopted }
  //
  // Deliberately NOT in `partialize` below. A preview is only meaningful against the
  // catalog snapshot it was taken from, so it must die with the page rather than be
  // rehydrated hours later and used to price a sale.
  pricePreview: null,

  // One entry per line the server re-priced, for the cashier-facing notice.
  // [{ lineIndex, label, field, fromCents, toCents }]
  priceAdoptionNotices: [],

  /**
   * Read (minting if needed) the key for this cart version. Both the online POST
   * and the offline queue call this, so one cart version has exactly one key
   * whichever path it takes.
   * @returns {string}
   */
  getClientTransactionId: () => {
    const existing = get().clientTransactionId;
    if (existing) return existing;
    const minted = newClientTransactionId();
    set((state) => { state.clientTransactionId = minted; });
    return minted;
  },

  /** Drop the key so the next cart version gets a fresh one. */
  rotateClientTransactionId: () => set((state) => { state.clientTransactionId = null; }),

  /**
   * Record a server price-preview against the cart version it was taken for.
   * @param {{fingerprint: string, totals: Object, lines: Array<Object>, mismatches: Array<Object>}} preview
   */
  setPricePreview: (preview) => set((state) => {
    state.pricePreview = { ...preview, adopted: false };
  }),

  /** Forget the preview and any notice. Called when the tender modal closes. */
  clearPricePreview: () => set((state) => {
    state.pricePreview = null;
    state.priceAdoptionNotices = [];
  }),

  /**
   * Adopt the server's prices for the current cart.
   *
   * The server is the authority; the POS never argues with it. Two fields move:
   *
   *   unitPriceCents <- serverUnitPriceCents. Required, not cosmetic: the server
   *     price is min(client, catalog), so leaving the old price on the line would
   *     make the SUBMITTED body carry a price above catalog — the exact blocking
   *     mismatch a 409 fires on. Adopting the total without the line price would
   *     produce a sale that previews clean and then gets rejected at checkout.
   *
   *   unitCostCents <- catalogCostCents. Cost is never the client's to set (it
   *     drives margin and commission) and a stale cached cost is blocking on the
   *     server, yet no cashier can fix it from the cart. Adopting it turns an
   *     unfixable rejection into a silent correction.
   *
   * Because the server price is a min(), adoption can only ever LOWER a line — a
   * cashier is never surprised by a higher number at tender.
   *
   * Price is in the amount fingerprint and cost is not, so this rotates the
   * idempotency key exactly once: the amount really did change, so it is a new cart
   * version and a retry must not collide with the pre-adoption one.
   */
  adoptServerPricing: () => {
    const preview = get().pricePreview;
    if (!preview || preview.adopted || !Array.isArray(preview.lines)) return;

    const items = get().items || [];
    const notices = [];

    for (const line of preview.lines) {
      if (line.unverified) continue;
      const item = items[line.index];
      if (!item) continue;
      const label = item.sku || item.productName || `Line ${line.index + 1}`;

      if (
        Number.isFinite(line.serverUnitPriceCents) &&
        line.serverUnitPriceCents !== item.unitPriceCents
      ) {
        notices.push({
          lineIndex: line.index,
          label,
          field: 'price',
          fromCents: item.unitPriceCents,
          toCents: line.serverUnitPriceCents,
        });
      }
      // Cost changes are corrected but NOT announced: the cashier cannot act on a
      // cost and the customer never sees one. It belongs in the audit row, not on
      // the tender screen.
    }

    set((state) => {
      for (const line of preview.lines) {
        if (line.unverified) continue;
        const item = state.items[line.index];
        if (!item) continue;
        if (Number.isFinite(line.serverUnitPriceCents)) {
          item.unitPriceCents = line.serverUnitPriceCents;
        }
        if (Number.isFinite(line.catalogCostCents)) {
          item.unitCostCents = line.catalogCostCents;
        }
      }
      state.priceAdoptionNotices = notices;
    });

    // Re-fingerprint AFTER the mutation. The server's totals stay valid for the
    // adopted cart — recomputing min(catalog, catalog) yields the same numbers — so
    // re-stamping here keeps getAdoptedTotalCents() answering through the window
    // between adoption and the confirming preview, instead of falling back to the
    // client's total for a frame.
    const fingerprint = cartAmountFingerprint(get());
    set((state) => {
      state.pricePreview = { ...preview, fingerprint, adopted: true };
    });
  },

  /**
   * Is there a server preview for the cart AS IT STANDS RIGHT NOW?
   *
   * False the moment the cart moves away from the previewed version — which is the
   * honest answer for `pricePreviewed` on the body. A cashier who opens tender,
   * backs out, applies a discount and re-tenders under the step freeze submits a
   * total the server never saw, and the audit row should say so rather than inherit
   * the earlier preview's clean bill of health.
   * @returns {boolean}
   */
  isPricePreviewCurrent: () => {
    const p = get().pricePreview;
    if (!p || !p.totals || !Number.isFinite(p.totals.totalCents)) return false;
    return cartAmountFingerprint(get()) === p.fingerprint;
  },

  /**
   * The server's total for the cart as it stands, or null if there isn't one.
   *
   * The client total is the fallback, not a competitor: the two legitimately differ
   * because the browser's tax is a hardcoded rate over the subtotal while the
   * server's is DB-driven over subtotal + EHF, so adopting line prices alone can
   * never reconcile them.
   * @returns {number|null} cents
   */
  getAdoptedTotalCents: () =>
    (get().isPricePreviewCurrent() ? get().pricePreview.totals.totalCents : null),

  // Actions
  addItem: (product, quantity = 1, options = {}) => set((state) => {
    // A warranty may only enter the cart through the upsell, which is the one
    // path that knows which item the plan covers and sets options.isWarranty.
    //
    // Added from catalogue search, a plan looks like any other $399.99 product:
    // it rings, it is charged, and it produces no coverage, because the recorder
    // gates on a flag only the upsell sets. That is TXN-20260904-0002. The server
    // now refuses such a sale outright; blocking here is what turns that refusal
    // into something the cashier can act on at the moment of the mistake rather
    // than at the moment of payment.
    //
    // This is the single chokepoint: search, product grid, variant picker,
    // barcode scan and bulk add all funnel through addItem.
    if (!options.isWarranty && isWarrantyProduct(product)) {
      state.error = "That's a warranty plan — add it from the warranty prompt on the item it covers, not from search.";
      return;
    }

    const existingIndex = state.items.findIndex(
      (item) => item.productId === product.id && !item.serialNumber && !options.serialNumber
    );

    // Resolve price + cost up-front; used by both the existing-item self-heal
    // path and the new-item path.
    //
    // Mirrors ProductTile.jsx fallback order: cents-authoritative first, then
    // dollar-denominated legacy fields (offline-cache or pre-Phase-2c rows).
    // FIXME: extract shared getProductPriceCents() consumed by tile + cart.
    //
    // Cents-column normalizer: pg returns BIGINT as STRING (e.g. msrp_cents
    // arrives as "399997"). Stringy values are truthy in `||` chains and
    // numerically-coercible in `<=` and `/`, so the symptom is "looks like
    // it works", but downstream consumers that strict-check Number lose.
    // Force a real number here.
    const centsToNum = (v) => {
      if (v == null || v === '') return 0;
      const n = typeof v === 'number' ? v : parseInt(v, 10);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };
    const dollarsToCents = (v) => {
      if (v == null || v === '') return 0;
      const n = parseFloat(v);
      return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
    };
    const customerPricing = get().customerPricing;
    const customPrice = customerPricing?.products?.[product.id];
    const resolvedUnitCents = centsToNum(customPrice?.priceCents)
      || centsToNum(product.sell_cents) || centsToNum(product.sellCents)
      || centsToNum(product.msrp_cents) || centsToNum(product.msrpCents)
      || dollarsToCents(product.unitPrice)
      || dollarsToCents(product.unit_price)
      || dollarsToCents(product.price)
      || 0;
    const resolvedOriginalCents = centsToNum(product.sell_cents) || centsToNum(product.sellCents)
      || centsToNum(product.msrp_cents) || centsToNum(product.msrpCents)
      || dollarsToCents(product.unitPrice)
      || dollarsToCents(product.unit_price)
      || dollarsToCents(product.price)
      || 0;
    const resolvedCostCents = centsToNum(product.cost_cents) || centsToNum(product.costCents)
      || dollarsToCents(product.cost)
      || 0;

    // Warranty add-ons NEVER merge into an existing line: each covers a specific item
    // (its own coversItemId) and must carry the isWarranty flag the recorder gates on.
    // The existing-item branch below does not set that metadata, so a merged warranty
    // would silently lose it (a genuine same-covered-item duplicate is rejected later by
    // migration 308's UNIQUE(transaction_id, covered_item_id)).
    if (existingIndex >= 0 && !options.forceNew && !options.isWarranty) {
      // Increment quantity for existing item
      state.items[existingIndex].quantity += quantity;

      // Self-heal: if a previously-persisted item has zero/missing price (likely
      // from a pre-fix code path or stale cart), and we now have a non-zero
      // resolved price, refresh it. Skip when the user has explicitly overridden.
      if (!state.items[existingIndex].priceOverride
          && (!state.items[existingIndex].unitPriceCents || state.items[existingIndex].unitPriceCents <= 0)
          && resolvedUnitCents > 0) {
        state.items[existingIndex].unitPriceCents = resolvedUnitCents;
        state.items[existingIndex].originalPriceCents = resolvedOriginalCents || resolvedUnitCents;
        if (!state.items[existingIndex].unitCostCents && resolvedCostCents > 0) {
          state.items[existingIndex].unitCostCents = resolvedCostCents;
        }
      }

      // Backfill screen_size_inches and category if missing on persisted item (display
      // only; the EHF comes from the server — see serverEhf).
      if (state.items[existingIndex].screen_size_inches == null
          && (product.screen_size_inches != null || product.screenSizeInches != null)) {
        state.items[existingIndex].screen_size_inches =
          product.screen_size_inches ?? product.screenSizeInches;
      }
      if (state.items[existingIndex].category == null) {
        state.items[existingIndex].category =
          product.category_name || product.legacy_category || null;
      }
    } else {
      // Add new item
      state.items.push({
        id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        productId: product.id,
        productName: product.name,
        sku: product.model || product.sku,
        barcode: product.barcode,
        imageUrl: product.image_url || product.imageUrl,
        // Display only. The line's EHF is the server's (serverEhf), never
        // derived from these.
        screen_size_inches: product.screen_size_inches ?? product.screenSizeInches ?? null,
        category: product.category_name || product.legacy_category || null,
        quantity,
        unitPriceCents: resolvedUnitCents,
        unitCostCents: resolvedCostCents,
        originalPriceCents: resolvedOriginalCents,
        discountPercent: options.discountPercent || 0,
        discountAmountCents: options.discountAmountCents || 0,
        discountReason: options.discountReason || '',
        taxable: product.taxable !== false,
        serialNumber: options.serialNumber || null,
        notes: options.notes || '',
        // POS warranty-at-sale metadata (Phase 4). Persisted from options so the
        // checkout payload can thread it to the backend. warrantyId is the
        // warranty_products.id; coversItemId is the covered line's cart id.
        isWarranty: options.isWarranty || false,
        coversItemId: options.coversItemId ?? null,
        warrantyId: options.warrantyId ?? null,
        addedAt: new Date().toISOString(),
      });
    }
  }),

  updateItem: (itemId, updates) => set((state) => {
    const index = state.items.findIndex((item) => item.id === itemId);
    if (index >= 0) {
      state.items[index] = { ...state.items[index], ...updates };
    }
  }),

  removeItem: (itemId) => set((state) => {
    state.items = state.items.filter((item) => item.id !== itemId);
  }),

  setItemQuantity: (itemId, quantity) => set((state) => {
    const index = state.items.findIndex((item) => item.id === itemId);
    if (index >= 0) {
      if (quantity <= 0) {
        state.items = state.items.filter((item) => item.id !== itemId);
      } else {
        state.items[index].quantity = quantity;
      }
    }
  }),

  // approvalInfo mirrors updateItemPrice: when a manager-approved override
  // lands via the discount-slider entry point, the approval token must reach
  // the item so getTransactionData can flatten it for the checkout gate
  // (previously the 4th arg was silently dropped and the gate rejected the line).
  applyItemDiscount: (itemId, discountPercent, reason = '', approvalInfo = null) => set((state) => {
    const index = state.items.findIndex((item) => item.id === itemId);
    if (index >= 0) {
      state.items[index].discountPercent = discountPercent;
      state.items[index].discountAmountCents = 0; // Clear fixed discount
      state.items[index].discountReason = reason;
      if (approvalInfo) state.items[index].approvalInfo = approvalInfo;
    }
  }),

  setItemSerialNumber: (itemId, serialNumber) => set((state) => {
    const index = state.items.findIndex((item) => item.id === itemId);
    if (index >= 0) {
      state.items[index].serialNumber = serialNumber;
    }
  }),

  // Convenience item actions (CartContext compatibility)
  removeItemByProductId: (productId) => set((state) => {
    const idx = state.items.findIndex((i) => i.productId === productId);
    if (idx >= 0) state.items.splice(idx, 1);
  }),

  incrementQuantity: (itemId) => set((state) => {
    const item = state.items.find((i) => i.id === itemId);
    if (item) item.quantity += 1;
  }),

  decrementQuantity: (itemId) => set((state) => {
    const idx = state.items.findIndex((i) => i.id === itemId);
    if (idx >= 0) {
      if (state.items[idx].quantity <= 1) {
        state.items.splice(idx, 1);
      } else {
        state.items[idx].quantity -= 1;
      }
    }
  }),

  updateItemPrice: (itemId, newPriceCents, reason = '', approvalInfo = null) => set((state) => {
    const item = state.items.find((i) => i.id === itemId);
    if (item) {
      item.priceOverride = true;
      item.priceOverrideReason = reason;
      item.unitPriceCents = newPriceCents;
      if (approvalInfo) item.approvalInfo = approvalInfo;
    }
  }),

  // Durable cart-item <-> approval-request link (Phase 2.5). The transient flow
  // state (approvalFlowItem / useApprovalFlow) dies on overlay close or remount,
  // abandoning still-pending requests client-side. The stamp rides the persisted
  // items array so the reconcile watcher can keep checking the server until a
  // terminal status. Shape: { status, requestId, cartItemId, requestedPrice,
  // createdAt, ... } — status starts 'pending'; the watcher marks 'approved'
  // (with resolution fields) or clears the stamp on denied/cancelled/timed_out.
  setItemPendingApproval: (itemId, pendingApproval) => set((state) => {
    const item = state.items.find((i) => i.id === itemId);
    if (item) item.pendingApproval = { status: 'pending', ...pendingApproval };
  }),

  updateItemPendingApproval: (itemId, updates) => set((state) => {
    const item = state.items.find((i) => i.id === itemId);
    if (item && item.pendingApproval) Object.assign(item.pendingApproval, updates);
  }),

  clearItemPendingApproval: (itemId) => set((state) => {
    const item = state.items.find((i) => i.id === itemId);
    if (item && item.pendingApproval !== undefined) delete item.pendingApproval;
  }),

  // Fulfillment
  setFulfillment: (fulfillment) => set((state) => { state.selectedFulfillment = fulfillment; }),
  clearFulfillment: () => set((state) => { state.selectedFulfillment = null; }),

  // Trade-ins
  addTradeIn: (tradeIn) => set((state) => { state.tradeIns.push(tradeIn); }),
  removeTradeIn: (tradeInId) => set((state) => {
    state.tradeIns = state.tradeIns.filter((t) => (t.id || t.tradeInId) !== tradeInId);
  }),
  clearTradeIns: () => set((state) => { state.tradeIns = []; }),

  // Commission
  setCommissionSplit: (config) => set((state) => { state.commissionSplit = config; }),

  // Loading/error
  setLoading: (loading) => set((state) => { state.loading = loading; }),
  setError: (error) => set((state) => { state.error = error; }),
  clearError: () => set((state) => { state.error = null; }),

  // Quote financing display
  setQuoteFinancingInfo: (info) => set((state) => { state.quoteFinancingInfo = info; }),

  setCartOwner: (userId) => set((state) => {
    state.cartOwnerId = userId ?? null;
  }),

  clearCart: () => {
    set((state) => {
      state.items = [];
      state.quoteId = null;
      state.quoteNumber = null;
      state.sourceType = null;
      state.notes = '';
      state.internalNotes = '';
      state.selectedFulfillment = null;
      state.tradeIns = [];
      state.commissionSplit = null;
      state.quoteFinancingInfo = null;
      state.error = null;
      // Keep salesperson
    });
    // Discount state must die with the cart it applied to — leaving it
    // produced a phantom cart-level discount on the NEXT sale (floor-tested
    // "-$40"). clearDiscounts is the single source of truth for zeroed
    // discount state (cartDiscount, appliedPromotions, manualAdjustments).
    get().clearDiscounts();
    // Same argument for the server price-preview: it was taken for the cart that
    // just went away, and the next customer's sale must not inherit it.
    get().clearPricePreview();
  },

  loadFromQuote: (quote) => set((state) => {
    state.quoteId = quote.id;
    state.quoteNumber = quote.quote_number || quote.quoteNumber;
    state.sourceType = 'quote';
    state.notes = quote.notes || '';
    state.internalNotes = quote.internal_notes || quote.internalNotes || '';

    // Set customer if available
    if (quote.customer) {
      state.customer = quote.customer;
    }

    // Map quote items to cart items
    state.items = (quote.items || []).map((item, index) => ({
      id: `quote-item-${quote.id}-${index}`,
      productId: item.product_id || item.productId,
      productName: item.product_name || item.productName || item.name,
      sku: item.sku || item.model,
      quantity: item.quantity,
      unitPriceCents: item.unit_price_cents || item.unitPriceCents ||
        Math.round((item.unit_price || item.unitPrice || 0) * 100),
      unitCostCents: item.unit_cost_cents || item.unitCostCents ||
        Math.round((item.unit_cost || item.unitCost || 0) * 100),
      originalPriceCents: item.original_price_cents || item.originalPriceCents ||
        item.unit_price_cents || Math.round((item.unit_price || 0) * 100),
      discountPercent: item.discount_percent || item.discountPercent || 0,
      discountAmountCents: item.discount_amount_cents || item.discountAmountCents || 0,
      taxable: item.taxable !== false,
      serialNumber: item.serial_number || item.serialNumber || null,
      notes: item.notes || '',
      addedAt: new Date().toISOString(),
    }));
  }),

  setNotes: (notes) => set((state) => {
    state.notes = notes;
  }),

  setInternalNotes: (notes) => set((state) => {
    state.internalNotes = notes;
  }),

  setSalesperson: (id, name) => set((state) => {
    state.salespersonId = id;
    state.salespersonName = name;
  }),
});

// ============================================================================
// DISCOUNT SLICE
// ============================================================================

const createDiscountSlice = (set) => ({
  // State
  cartDiscount: {
    type: 'percent', // 'percent' | 'fixed'
    value: 0,
    reason: '',
  },
  appliedPromotions: [], // Auto-applied promotions
  manualAdjustments: [], // Manager overrides

  // Actions
  setCartDiscount: (type, value, reason = '') => set((state) => {
    state.cartDiscount = { type, value, reason };
  }),

  clearCartDiscount: () => set((state) => {
    state.cartDiscount = { type: 'percent', value: 0, reason: '' };
  }),

  addPromotion: (promotion) => set((state) => {
    // Avoid duplicates
    if (!state.appliedPromotions.find((p) => p.id === promotion.id)) {
      state.appliedPromotions.push(promotion);
    }
  }),

  removePromotion: (promotionId) => set((state) => {
    state.appliedPromotions = state.appliedPromotions.filter((p) => p.id !== promotionId);
  }),

  addManualAdjustment: (adjustment) => set((state) => {
    state.manualAdjustments.push({
      ...adjustment,
      id: `adj-${Date.now()}`,
      appliedAt: new Date().toISOString(),
    });
  }),

  clearDiscounts: () => set((state) => {
    state.cartDiscount = { type: 'percent', value: 0, reason: '' };
    state.appliedPromotions = [];
    state.manualAdjustments = [];
  }),
});

/**
 * The key a line's server EHF is filed under: province, product and whether the line is a
 * warranty (a plan carries no fee; it is keyed apart so it can never share an answer with a
 * product). null for a line with no product — a custom line carries no fee and needs no answer.
 */
export function ehfLineKey(item, province) {
  if (!item || item.productId == null || item.productId === '') return null;
  const warranty = item.isWarranty || String(item.sku || '').toLowerCase().startsWith('wrn-');
  return `${province || 'ON'}:${item.productId}:${warranty ? 'w' : 'p'}`;
}

// ============================================================================
// PRICING SLICE (Computed values + tax settings)
// ============================================================================

const createPricingSlice = (set, get) => ({
  // State
  taxProvince: 'ON',
  taxExempt: false,
  taxExemptReason: '',
  currency: 'CAD',

  // The server's per-unit EHF, in cents, keyed by ehfLineKey(item, province). Filled by
  // ehfSync from POST /api/tax/ehf/resolve — the same resolver the recorder uses, so the
  // total tendered here is the total the recorder expects. Persisted: a recalled cart or an
  // offline sale keeps what the server said. A line with no entry charges nothing and holds
  // tender (tenderGate) until the server has answered; it is never guessed.
  serverEhf: {},
  // 'idle' | 'loading' | 'ok' | 'unreachable' — the last EHF request. Not persisted.
  ehfStatus: 'idle',
  // Bumped by retryEhf so ehfSync asks again after a failure.
  ehfRetryToken: 0,

  // Actions
  setTaxProvince: (province) => set((state) => {
    state.taxProvince = province;
  }),

  setTaxExempt: (exempt, reason = '') => set((state) => {
    state.taxExempt = exempt;
    state.taxExemptReason = reason;
  }),

  /** Record the server's answers: [{ key, ehfCents }]. */
  setServerEhf: (entries) => set((state) => {
    for (const { key, ehfCents } of entries || []) {
      if (key && Number.isInteger(ehfCents) && ehfCents >= 0) state.serverEhf[key] = ehfCents;
    }
  }),

  clearServerEhf: () => set((state) => {
    state.serverEhf = {};
    state.ehfStatus = 'idle';
  }),

  setEhfStatus: (status) => set((state) => { state.ehfStatus = status; }),

  retryEhf: () => set((state) => {
    state.ehfStatus = 'idle';
    state.ehfRetryToken += 1;
  }),

  /**
   * Lines the server hasn't given an EHF for yet, one per key (a product in two lines is
   * asked about once). Name and SKU go with it so the server applies its own warranty rule.
   * @returns {Array<{key:string, productId:number, name:string, sku:string}>}
   */
  getUnresolvedEhfLines: () => {
    const { items, taxProvince, serverEhf } = get();
    const seen = new Set();
    const out = [];
    for (const item of items) {
      const key = ehfLineKey(item, taxProvince);
      if (!key || key in serverEhf || seen.has(key)) continue;
      seen.add(key);
      out.push({ key, productId: item.productId, name: item.productName || '', sku: item.sku || '' });
    }
    return out;
  },

  isEhfResolved: () => get().getUnresolvedEhfLines().length === 0,

  // Computed getters (called as functions)
  getSubtotal: () => {
    const { items } = get();
    return items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  },

  getItemDiscountTotal: () => {
    const { items } = get();
    return items.reduce((sum, item) => {
      const basePrice = item.unitPriceCents * item.quantity;
      const discountAmount = item.discountPercent
        ? Math.round(basePrice * (item.discountPercent / 100))
        : (item.discountAmountCents || 0);
      return sum + discountAmount;
    }, 0);
  },

  getCartDiscountAmount: () => {
    const { cartDiscount } = get();
    const subtotal = get().getSubtotal();

    if (cartDiscount.type === 'percent') {
      return Math.round(subtotal * (cartDiscount.value / 100));
    }
    return Math.round(cartDiscount.value * 100); // Convert dollars to cents
  },

  getTaxableAmount: () => {
    const { items, taxExempt } = get();
    if (taxExempt) return 0;

    const subtotal = get().getSubtotal();
    const cartDiscountAmount = get().getCartDiscountAmount();
    const discountedSubtotal = subtotal - cartDiscountAmount;

    // Calculate taxable portion
    const taxableItems = items.filter((item) => item.taxable);
    const taxableSubtotal = taxableItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);

    // Proportionally apply cart discount to taxable amount
    const taxableRatio = subtotal > 0 ? taxableSubtotal / subtotal : 0;
    const itemsTaxable = Math.round(discountedSubtotal * taxableRatio);

    // EHF is taxable in Ontario per the backend's authoritative tax rule
    // (taxService computes tax over subtotal+EHF). Including it here keeps
    // frontend cart.total in lockstep with the backend transaction total —
    // otherwise checkout fails with "Payment total does not match
    // transaction total" by ~13% of EHF.
    const ehfCents = Math.round(get().getEHFTotal() * 100);
    return itemsTaxable + ehfCents;
  },

  getTaxBreakdown: () => {
    const { taxProvince, taxExempt } = get();
    if (taxExempt) {
      return { hst: 0, gst: 0, pst: 0, qst: 0, total: 0, label: 'Tax Exempt' };
    }

    const taxableAmount = get().getTaxableAmount();
    return calculateTax(taxableAmount, taxProvince);
  },

  /**
   * The cart's EHF in dollars: each line's per-unit fee as the SERVER resolved it (see
   * serverEhf), times its quantity. No product-name rules — the till used to add its own
   * by-name figure, and the recorder refused every sale where the two disagreed.
   */
  getEHFTotal: () => {
    const { items, taxProvince, serverEhf } = get();
    let cents = 0;
    for (const item of items) {
      const key = ehfLineKey(item, taxProvince);
      if (!key) continue;
      cents += (serverEhf[key] || 0) * (item.quantity || 0);
    }
    return cents / 100;
  },

  getTotal: () => {
    const subtotal = get().getSubtotal();
    const cartDiscountAmount = get().getCartDiscountAmount();
    const ehfCents = Math.round(get().getEHFTotal() * 100);
    const deliveryFee = get().selectedFulfillment?.fee || 0;
    const deliveryFeeCents = Math.round(deliveryFee * 100);
    const taxBreakdown = get().getTaxBreakdown();
    return subtotal - cartDiscountAmount + ehfCents + deliveryFeeCents + taxBreakdown.total;
  },

  getTotalCost: () => {
    const { items } = get();
    return items.reduce((sum, item) => sum + (item.unitCostCents * item.quantity), 0);
  },

  getMargin: () => {
    const revenue = get().getSubtotal() - get().getCartDiscountAmount();
    const cost = get().getTotalCost();
    return calculateMargin(revenue, cost);
  },

  getItemCount: () => {
    const { items } = get();
    return items.reduce((sum, item) => sum + item.quantity, 0);
  },

  getSummary: () => {
    const state = get();
    const taxBreakdown = state.getTaxBreakdown();
    const ehfTotal = state.getEHFTotal();
    const deliveryFee = state.selectedFulfillment?.fee || 0;
    const tradeInTotal = state.tradeIns.reduce((s, t) => s + (parseFloat(t.final_value || t.finalValue) || 0), 0);
    const totalCents = state.getTotal();
    const amountToPay = Math.max(0, totalCents / 100 - tradeInTotal);
    return {
      itemCount: state.getItemCount(),
      lineCount: state.items.length,
      subtotalCents: state.getSubtotal(),
      itemDiscountCents: state.getItemDiscountTotal(),
      cartDiscountCents: state.getCartDiscountAmount(),
      taxableAmountCents: state.getTaxableAmount(),
      taxBreakdown,
      taxCents: taxBreakdown.total,
      ehfTotal,
      deliveryFee,
      totalCents,
      totalCostCents: state.getTotalCost(),
      marginPercent: state.getMargin(),
      tradeInTotal,
      amountToPay,
      isEmpty: state.items.length === 0,
      // Formatted values
      subtotal: (state.getSubtotal() / 100).toFixed(2),
      total: (totalCents / 100).toFixed(2),
      tax: (taxBreakdown.total / 100).toFixed(2),
    };
  },
});

// ============================================================================
// DRAFT SLICE
// ============================================================================

const createDraftSlice = (set, get) => ({
  // State
  draftId: null,
  draftType: null, // 'quote' | 'pos' | 'order'
  lastSavedAt: null,
  isDirty: false,
  autoSaveEnabled: true,
  syncStatus: 'synced', // 'synced' | 'pending' | 'error'

  // Actions
  setDraftId: (id, type) => set((state) => {
    state.draftId = id;
    state.draftType = type;
  }),

  markDirty: () => set((state) => {
    state.isDirty = true;
  }),

  markSaved: (timestamp = new Date().toISOString()) => set((state) => {
    state.isDirty = false;
    state.lastSavedAt = timestamp;
  }),

  setSyncStatus: (status) => set((state) => {
    state.syncStatus = status;
  }),

  setAutoSave: (enabled) => set((state) => {
    state.autoSaveEnabled = enabled;
  }),

  // Generate draft snapshot for saving
  getDraftSnapshot: () => {
    const state = get();
    return {
      // Cart data
      items: state.items,
      quoteId: state.quoteId,
      quoteNumber: state.quoteNumber,
      sourceType: state.sourceType,
      notes: state.notes,
      internalNotes: state.internalNotes,
      salespersonId: state.salespersonId,
      salespersonName: state.salespersonName,
      selectedFulfillment: state.selectedFulfillment,
      tradeIns: state.tradeIns,
      commissionSplit: state.commissionSplit,
      quoteFinancingInfo: state.quoteFinancingInfo,
      // Customer
      customer: state.customer,
      customerPricing: state.customerPricing,
      // Discounts
      cartDiscount: state.cartDiscount,
      appliedPromotions: state.appliedPromotions,
      manualAdjustments: state.manualAdjustments,
      // Pricing
      taxProvince: state.taxProvince,
      taxExempt: state.taxExempt,
      taxExemptReason: state.taxExemptReason,
      // Metadata
      draftType: state.draftType,
      savedAt: new Date().toISOString(),
    };
  },

  // Restore from draft snapshot
  restoreFromDraft: (draft) => set((state) => {
    // Cart data
    state.items = draft.items || [];
    state.quoteId = draft.quoteId || null;
    state.quoteNumber = draft.quoteNumber || null;
    state.sourceType = draft.sourceType || null;
    state.notes = draft.notes || '';
    state.internalNotes = draft.internalNotes || '';
    state.salespersonId = draft.salespersonId || null;
    state.salespersonName = draft.salespersonName || '';
    state.selectedFulfillment = draft.selectedFulfillment || null;
    state.tradeIns = draft.tradeIns || [];
    state.commissionSplit = draft.commissionSplit || null;
    state.quoteFinancingInfo = draft.quoteFinancingInfo || null;
    // Customer
    state.customer = draft.customer || null;
    state.customerPricing = draft.customerPricing || null;
    // Discounts
    state.cartDiscount = draft.cartDiscount || { type: 'percent', value: 0, reason: '' };
    state.appliedPromotions = draft.appliedPromotions || [];
    state.manualAdjustments = draft.manualAdjustments || [];
    // Pricing
    state.taxProvince = draft.taxProvince || 'ON';
    state.taxExempt = draft.taxExempt || false;
    state.taxExemptReason = draft.taxExemptReason || '';
    // Metadata
    state.draftType = draft.draftType || null;
    state.isDirty = false;
    state.lastSavedAt = draft.savedAt || null;
  }),

  // Clear all state
  resetAll: () => set((state) => {
    // Cart
    state.items = [];
    state.quoteId = null;
    state.quoteNumber = null;
    state.sourceType = null;
    state.notes = '';
    state.internalNotes = '';
    state.selectedFulfillment = null;
    state.tradeIns = [];
    state.commissionSplit = null;
    state.quoteFinancingInfo = null;
    state.error = null;
    state.loading = false;
    // Customer
    state.customer = null;
    state.customerPricing = null;
    state.customerHistory = [];
    // Discounts
    state.cartDiscount = { type: 'percent', value: 0, reason: '' };
    state.appliedPromotions = [];
    state.manualAdjustments = [];
    // Pricing
    state.taxExempt = false;
    state.taxExemptReason = '';
    // Draft
    state.draftId = null;
    state.draftType = null;
    state.isDirty = false;
    state.lastSavedAt = null;
    state.syncStatus = 'synced';
  }),
});

// ============================================================================
// HELD TRANSACTIONS SLICE (POS-specific)
// ============================================================================

const createHeldTransactionsSlice = (set, get) => ({
  // State
  heldTransactions: [],
  maxHeldTransactions: 10,

  // Actions
  holdCurrentTransaction: (label = '') => {
    const state = get();
    if (state.items.length === 0) return false;

    const heldTransaction = {
      id: `held-${Date.now()}`,
      label: label || `Transaction ${state.heldTransactions.length + 1}`,
      snapshot: state.getDraftSnapshot(),
      heldAt: new Date().toISOString(),
      itemCount: state.getItemCount(),
      totalCents: state.getTotal(),
      customerName: state.customer?.name || null,
    };

    set((s) => {
      if (s.heldTransactions.length >= s.maxHeldTransactions) {
        // Remove oldest
        s.heldTransactions.shift();
      }
      s.heldTransactions.push(heldTransaction);
    });

    // Clear current cart
    get().resetAll();
    return true;
  },

  recallTransaction: (heldId) => {
    const state = get();
    const held = state.heldTransactions.find((t) => t.id === heldId);
    if (!held) return false;

    // If current cart has items, hold it first
    if (state.items.length > 0) {
      state.holdCurrentTransaction('Auto-held');
    }

    // Restore the held transaction
    state.restoreFromDraft(held.snapshot);

    // Remove from held list
    set((s) => {
      s.heldTransactions = s.heldTransactions.filter((t) => t.id !== heldId);
    });

    return true;
  },

  deleteHeldTransaction: (heldId) => set((state) => {
    state.heldTransactions = state.heldTransactions.filter((t) => t.id !== heldId);
  }),

  clearAllHeld: () => set((state) => {
    state.heldTransactions = [];
  }),
});

// ============================================================================
// SYNC SLICE (Online/Offline)
// ============================================================================

const createSyncSlice = (set) => ({
  // State
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  pendingOperations: [],
  lastSyncAt: null,
  syncError: null,

  // Actions
  setOnline: (online) => set((state) => {
    state.isOnline = online;
  }),

  addPendingOperation: (operation) => set((state) => {
    state.pendingOperations.push({
      ...operation,
      id: `op-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      retryCount: 0,
    });
    state.syncStatus = 'pending';
  }),

  removePendingOperation: (operationId) => set((state) => {
    state.pendingOperations = state.pendingOperations.filter((op) => op.id !== operationId);
    if (state.pendingOperations.length === 0) {
      state.syncStatus = 'synced';
    }
  }),

  incrementRetryCount: (operationId) => set((state) => {
    const op = state.pendingOperations.find((o) => o.id === operationId);
    if (op) {
      op.retryCount++;
    }
  }),

  setSyncError: (error) => set((state) => {
    state.syncError = error;
    state.syncStatus = 'error';
  }),

  clearSyncError: () => set((state) => {
    state.syncError = null;
  }),

  markSynced: () => set((state) => {
    state.lastSyncAt = new Date().toISOString();
    state.syncStatus = 'synced';
  }),
});

// ============================================================================
// CREATE UNIFIED STORE
// ============================================================================

// Repair cart items persisted before the warranty-add signature fix (fe940fb).
// The prior 2-arg call `addItem(product, {quantity:1, isWarranty, coversItemId})`
// bound the options object to the `quantity` PARAMETER, so the item was stored with
// `quantity` = that object. Rendering {item.quantity} (an object) crashes the cart
// with React #31 ("object with key quantity") on rehydration. Coerce quantity back
// to a number and lift any warranty metadata trapped inside the object.
export function sanitizeCartItem(item) {
  if (!item) return item;
  if (typeof item.quantity === 'object' && item.quantity !== null) {
    const q = item.quantity;
    return {
      ...item,
      quantity: Number(q.quantity) > 0 ? Number(q.quantity) : 1,
      isWarranty: item.isWarranty || q.isWarranty || false,
      coversItemId: item.coversItemId ?? q.coversItemId ?? null,
      warrantyId: item.warrantyId ?? q.warrantyId ?? null
    };
  }
  const n = Number(item.quantity);
  return { ...item, quantity: Number.isFinite(n) && n > 0 ? n : 1 };
}

export const useUnifiedStore = create(
  subscribeWithSelector(
    persist(
      immer((set, get) => ({
        // Combine all slices
        ...createCustomerSlice(set, get),
        ...createCartSlice(set, get),
        ...createDiscountSlice(set, get),
        ...createPricingSlice(set, get),
        ...createDraftSlice(set, get),
        ...createHeldTransactionsSlice(set, get),
        ...createSyncSlice(set, get),
      })),
      {
        name: 'unified-store',
        storage: createIDBStorage(),
        partialize: (state) => ({
          // Persist these fields
          items: state.items,
          cartOwnerId: state.cartOwnerId,
          customer: state.customer,
          customerPricing: state.customerPricing,
          quoteId: state.quoteId,
          quoteNumber: state.quoteNumber,
          sourceType: state.sourceType,
          notes: state.notes,
          internalNotes: state.internalNotes,
          salespersonId: state.salespersonId,
          salespersonName: state.salespersonName,
          selectedFulfillment: state.selectedFulfillment,
          tradeIns: state.tradeIns,
          commissionSplit: state.commissionSplit,
          quoteFinancingInfo: state.quoteFinancingInfo,
          cartDiscount: state.cartDiscount,
          appliedPromotions: state.appliedPromotions,
          taxProvince: state.taxProvince,
          taxExempt: state.taxExempt,
          serverEhf: state.serverEhf,
          heldTransactions: state.heldTransactions,
          pendingOperations: state.pendingOperations,
          draftId: state.draftId,
          draftType: state.draftType,
        }),
        // Quarantine corrupted persisted cart items on rehydrate.
        // Items with non-positive unitPriceCents (e.g., from any code path that
        // wrote a stale/missing price) are dropped rather than restored — they
        // would only re-trigger "Price needed" / $0.00 / checkout-blocked. The
        // user can simply re-tap the product tile to add it fresh with the
        // current price-resolution chain. Belt-and-suspenders alongside the
        // self-heal in addItem's existing-item branch.
        merge: (persistedState, currentState) => {
          if (persistedState && Array.isArray(persistedState.items)) {
            persistedState.items = persistedState.items
              .filter((item) => item && Number(item.unitPriceCents) > 0)
              .map(sanitizeCartItem);
          }
          return { ...currentState, ...persistedState };
        },
      }
    )
  )
);

// ============================================================================
// IDEMPOTENCY KEY ROTATION
// ============================================================================

/**
 * Everything that changes what the customer owes. Anything NOT in here (customer,
 * salesperson, notes, tax province, hold/recall) leaves the key alone, because a
 * retry after one of those is still the same sale.
 *
 * EXPORTED because the price-preview hook (item 3c) needs the identical notion of
 * "the cart's amount changed" — a preview is stale under exactly the conditions that
 * make the idempotency key stale. Two subscribers, one definition: a second
 * hand-rolled change-detector would drift from this one the first time a new
 * amount-bearing field is added to only one of them.
 * @param {Object} s
 * @returns {string} a cheap fingerprint of the cart's amount-bearing state
 */
export const cartAmountFingerprint = (s) =>
  JSON.stringify([
    s.items?.map((i) => [i.id, i.productId, i.quantity, i.unitPriceCents, i.discountPercent, i.discountAmountCents]),
    s.cartDiscount,
    s.appliedPromotions?.map((p) => p.id),
    s.manualAdjustments,
    s.selectedFulfillment?.type,
    s.selectedFulfillment?.fee,
    s.tradeIns?.map((t) => t.assessmentId ?? t.id),
    s.quoteId,
  ]);

// One subscription instead of a rotate call inside thirteen actions — a new action
// that changes the amount rotates automatically, rather than silently reusing a key
// because someone forgot a line. Fires only when the fingerprint actually changes.
useUnifiedStore.subscribe(
  cartAmountFingerprint,
  () => {
    if (useUnifiedStore.getState().clientTransactionId !== null) {
      useUnifiedStore.setState({ clientTransactionId: null });
    }
  }
);

// ============================================================================
// SELECTOR HOOKS (For convenience)
// ============================================================================

// Customer-related state
export const useCustomer = () => useUnifiedStore((state) => ({
  customer: state.customer,
  customerPricing: state.customerPricing,
  customerHistory: state.customerHistory,
  setCustomer: state.setCustomer,
  setCustomerPricing: state.setCustomerPricing,
  clearCustomer: state.clearCustomer,
  loadCustomerHistory: state.loadCustomerHistory,
}));

// Cart/items state
export const useCart = () => useUnifiedStore((state) => ({
  items: state.items,
  quoteId: state.quoteId,
  quoteNumber: state.quoteNumber,
  sourceType: state.sourceType,
  notes: state.notes,
  salespersonId: state.salespersonId,
  salespersonName: state.salespersonName,
  commissionSplit: state.commissionSplit,
  selectedFulfillment: state.selectedFulfillment,
  tradeIns: state.tradeIns,
  loading: state.loading,
  error: state.error,
  quoteFinancingInfo: state.quoteFinancingInfo,
  // Item actions
  addItem: state.addItem,
  updateItem: state.updateItem,
  removeItem: state.removeItem,
  removeItemByProductId: state.removeItemByProductId,
  setItemQuantity: state.setItemQuantity,
  incrementQuantity: state.incrementQuantity,
  decrementQuantity: state.decrementQuantity,
  applyItemDiscount: state.applyItemDiscount,
  updateItemPrice: state.updateItemPrice,
  setItemSerialNumber: state.setItemSerialNumber,
  clearCart: state.clearCart,
  loadFromQuote: state.loadFromQuote,
  setNotes: state.setNotes,
  setSalesperson: state.setSalesperson,
  // Fulfillment
  setFulfillment: state.setFulfillment,
  clearFulfillment: state.clearFulfillment,
  // Trade-ins
  addTradeIn: state.addTradeIn,
  removeTradeIn: state.removeTradeIn,
  clearTradeIns: state.clearTradeIns,
  // Commission
  setCommissionSplit: state.setCommissionSplit,
  // Loading/error
  setLoading: state.setLoading,
  setError: state.setError,
  clearError: state.clearError,
  // Quote financing
  setQuoteFinancingInfo: state.setQuoteFinancingInfo,
  // Held transactions
  heldTransactions: state.heldTransactions,
  holdCurrentTransaction: state.holdCurrentTransaction,
  recallTransaction: state.recallTransaction,
  deleteHeldTransaction: state.deleteHeldTransaction,
}));

// Pricing/calculations
export const usePricing = () => {
  const store = useUnifiedStore();
  return {
    taxProvince: store.taxProvince,
    taxExempt: store.taxExempt,
    currency: store.currency,
    setTaxProvince: store.setTaxProvince,
    setTaxExempt: store.setTaxExempt,
    // Discounts
    cartDiscount: store.cartDiscount,
    appliedPromotions: store.appliedPromotions,
    setCartDiscount: store.setCartDiscount,
    clearCartDiscount: store.clearCartDiscount,
    addPromotion: store.addPromotion,
    removePromotion: store.removePromotion,
    // Computed values
    summary: store.getSummary(),
  };
};

// Draft management
export const useDrafts = () => useUnifiedStore((state) => ({
  draftId: state.draftId,
  draftType: state.draftType,
  lastSavedAt: state.lastSavedAt,
  isDirty: state.isDirty,
  autoSaveEnabled: state.autoSaveEnabled,
  syncStatus: state.syncStatus,
  setDraftId: state.setDraftId,
  markDirty: state.markDirty,
  markSaved: state.markSaved,
  setAutoSave: state.setAutoSave,
  getDraftSnapshot: state.getDraftSnapshot,
  restoreFromDraft: state.restoreFromDraft,
  resetAll: state.resetAll,
}));

// Sync status
export const useSync = () => useUnifiedStore((state) => ({
  isOnline: state.isOnline,
  pendingOperations: state.pendingOperations,
  lastSyncAt: state.lastSyncAt,
  syncError: state.syncError,
  syncStatus: state.syncStatus,
  setOnline: state.setOnline,
  addPendingOperation: state.addPendingOperation,
  removePendingOperation: state.removePendingOperation,
  setSyncError: state.setSyncError,
  clearSyncError: state.clearSyncError,
  markSynced: state.markSynced,
}));
